import { useState } from 'react';
import {
  Search, Plus, Edit2, Trash2, GripVertical, Tag, Package,
  ToggleLeft, ToggleRight, X, Check,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { mockCategories } from '@/services/mockData';

export default function CategoriesPage() {
  const { t, language } = useTranslation();
  const [categories, setCategories] = useState(mockCategories);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const filteredCategories = categories.filter((cat) => {
    return !searchQuery ||
      cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.name_en.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const activeCount = categories.filter(c => c.status === 'active').length;
  const totalProducts = categories.reduce((s, c) => s + c.products_count, 0);

  const toggleStatus = (id: number) => {
    setCategories(prev => prev.map(c =>
      c.id === id ? { ...c, status: c.status === 'active' ? 'inactive' : 'active' } : c
    ));
  };

  const deleteCategory = (id: number) => {
    setCategories(prev => prev.filter(c => c.id !== id));
    setDeleteConfirm(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('categories')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar'
              ? `${categories.length} تصنيف · ${activeCount} نشط · ${totalProducts} منتج`
              : `${categories.length} categories · ${activeCount} active · ${totalProducts} products`}
          </p>
        </div>
        <button
          onClick={() => { setEditingCategory(null); setShowAddModal(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25"
        >
          <Plus size={18} />
          {language === 'ar' ? 'إضافة تصنيف' : 'Add Category'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Tag size={22} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{categories.length}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {language === 'ar' ? 'إجمالي التصنيفات' : 'Total Categories'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Check size={22} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{activeCount}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {language === 'ar' ? 'تصنيفات نشطة' : 'Active Categories'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <Package size={22} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800 dark:text-white">{totalProducts}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {language === 'ar' ? 'إجمالي المنتجات' : 'Total Products'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="relative">
          <Search size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={language === 'ar' ? 'بحث في التصنيفات...' : 'Search categories...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full ps-10 pe-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-3">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className={`bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 transition-all hover:shadow-md ${
              category.status === 'inactive' ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Drag Handle */}
              <div className="hidden sm:flex items-center text-slate-300 dark:text-slate-600 cursor-grab">
                <GripVertical size={20} />
              </div>

              {/* Order Number */}
              <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center text-sm font-medium text-slate-500 dark:text-slate-400">
                {category.sort_order}
              </div>

              {/* Icon */}
              <div className="w-12 h-12 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-600 rounded-xl flex items-center justify-center text-2xl shadow-sm">
                {category.icon}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-800 dark:text-white">{category.name}</h3>
                  <span className="text-sm text-slate-400 dark:text-slate-500">({category.name_en})</span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <Package size={14} />
                    {category.products_count} {language === 'ar' ? 'منتج' : 'products'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    category.status === 'active'
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}>
                    {category.status === 'active' ? (language === 'ar' ? 'نشط' : 'Active') : (language === 'ar' ? 'غير نشط' : 'Inactive')}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {/* Toggle Status */}
                <button
                  onClick={() => toggleStatus(category.id)}
                  className="p-2 rounded-xl transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
                  title={category.status === 'active' ? (language === 'ar' ? 'إيقاف' : 'Deactivate') : (language === 'ar' ? 'تفعيل' : 'Activate')}
                >
                  {category.status === 'active' ? (
                    <ToggleRight size={22} className="text-emerald-500" />
                  ) : (
                    <ToggleLeft size={22} className="text-slate-400" />
                  )}
                </button>

                {/* Edit */}
                <button
                  onClick={() => { setEditingCategory(category); setShowAddModal(true); }}
                  className="p-2 rounded-xl text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  <Edit2 size={18} />
                </button>

                {/* Delete */}
                {deleteConfirm === category.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => deleteCategory(category.id)}
                      className="p-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Check size={18} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(category.id)}
                    className="p-2 rounded-xl text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredCategories.length === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Tag size={28} className="text-emerald-500" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">{t('noData')}</h3>
          <p className="text-slate-500 dark:text-slate-400">
            {language === 'ar' ? 'لم يتم العثور على تصنيفات' : 'No categories found'}
          </p>
        </div>
      )}

      {/* Add / Edit Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                  {editingCategory
                    ? (language === 'ar' ? 'تعديل التصنيف' : 'Edit Category')
                    : (language === 'ar' ? 'إضافة تصنيف جديد' : 'Add New Category')}
                </h2>
                <button onClick={() => setShowAddModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Icon Selector */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {language === 'ar' ? 'الأيقونة' : 'Icon'}
                </label>
                <div className="grid grid-cols-8 gap-2">
                  {['☕', '🧊', '🍵', '🥤', '🥝', '🍰', '🥐', '🥪', '🥛', '🫖', '🍳', '🎁', '🍕', '🍔', '🌮', '🍜'].map((icon) => (
                    <button
                      key={icon}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl transition-all ${
                        (editingCategory?.icon || '☕') === icon
                          ? 'bg-emerald-100 dark:bg-emerald-900/40 ring-2 ring-emerald-500 scale-110'
                          : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name AR */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الاسم (عربي)' : 'Name (Arabic)'}
                </label>
                <input
                  type="text"
                  defaultValue={editingCategory?.name || ''}
                  placeholder={language === 'ar' ? 'اسم التصنيف...' : 'Category name...'}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>

              {/* Name EN */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)'}
                </label>
                <input
                  type="text"
                  defaultValue={editingCategory?.name_en || ''}
                  placeholder="Category name..."
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'ترتيب العرض' : 'Sort Order'}
                </label>
                <input
                  type="number"
                  defaultValue={editingCategory?.sort_order || categories.length + 1}
                  min={1}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors font-medium"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-600 transition-all font-medium"
                >
                  {editingCategory ? t('save') : t('add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
